import React, { useEffect, useState } from 'react';
import { Eye, Users, TrendingUp, RefreshCw, Download, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { readJson } from '../lib/fetchHelpers';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, visits, unique, testid }) => (
  <div
    className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="bg-green-500/10 text-green-600 rounded-lg p-2">
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">
        {label}
      </p>
    </div>
    <p className="text-4xl font-bold text-slate-900" data-testid={`${testid}-visits`}>
      {visits.toLocaleString('nb-NO')}
    </p>
    <p className="text-sm text-slate-500 mt-1">
      <span className="font-semibold text-slate-700" data-testid={`${testid}-unique`}>
        {unique.toLocaleString('nb-NO')}
      </span>{' '}
      unike besøkende
    </p>
  </div>
);

export const AnalyticsTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const sendMonthlyReport = async () => {
    if (
      !window.confirm(
        'Send en månedsrapport på e-post nå?\n\nRapporten dekker forrige kalendermåned og sendes til nickolas.paynter@veitvet-sportsklubb.no. Hvis månedsrapporten allerede er sendt for samme periode, sendes en ny kopi.'
      )
    ) {
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${API_URL}/api/analytics/monthly-report/send-now?force=true`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(
        data.status === 'sent'
          ? 'Månedsrapport sendt til nickolas.paynter@veitvet-sportsklubb.no'
          : 'Forespørsel mottatt'
      );
    } catch (err) {
      toast.error('Kunne ikke sende månedsrapport');
    } finally {
      setExporting(false);
    }
  };

  const exportCsv = async (days, label) => {    setExporting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/analytics/export.csv?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vsk-besoksstatistikk-${label}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV lastet ned');
    } catch (err) {
      toast.error('Kunne ikke laste ned CSV');
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStats(await readJson(res));
    } catch (err) {
      setError('Kunne ikke hente statistikk. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500" data-testid="analytics-loading">
        Laster statistikk...
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={load} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" /> Prøv igjen
        </Button>
      </div>
    );
  }

  const maxDaily = Math.max(1, ...stats.daily_last_30_days.map((d) => d.visits));

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            Besøksstatistikk
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Anonyme tall – ingen IP eller personalia lagres. Synlig kun for admin.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={sendMonthlyReport}
            variant="outline"
            disabled={exporting}
            data-testid="analytics-send-monthly"
          >
            <Mail className="w-4 h-4 mr-2" />
            Send månedsrapport nå
          </Button>
          <Button
            onClick={() => exportCsv(30, '30dager')}
            variant="outline"
            disabled={exporting}
            data-testid="analytics-export-30d"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV (30d)
          </Button>
          <Button
            onClick={() => exportCsv(365, '365dager')}
            variant="outline"
            disabled={exporting}
            data-testid="analytics-export-365d"
          >
            <Download className="w-4 h-4 mr-2" />
            CSV (1 år)
          </Button>
          <Button onClick={load} variant="outline" data-testid="analytics-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Oppdater
          </Button>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-sm text-slate-700 flex items-start gap-3" data-testid="monthly-report-info">
        <Mail className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" />
        <span>
          <strong>Automatisk månedsrapport:</strong> Sendes til{' '}
          <code className="text-xs bg-white px-1 py-0.5 rounded border border-slate-200">
            nickolas.paynter@veitvet-sportsklubb.no
          </code>{' '}
          den 1. i hver måned med oppsummering av forrige kalendermåned – inkludert PDF-vedlegg.
        </span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label="I dag"
          visits={stats.today.visits}
          unique={stats.today.unique}
          testid="stat-today"
        />
        <StatCard
          icon={TrendingUp}
          label="Siste 7 dager"
          visits={stats.last_7_days.visits}
          unique={stats.last_7_days.unique}
          testid="stat-7d"
        />
        <StatCard
          icon={TrendingUp}
          label="Siste 30 dager"
          visits={stats.last_30_days.visits}
          unique={stats.last_30_days.unique}
          testid="stat-30d"
        />
        <StatCard
          icon={Users}
          label="Totalt"
          visits={stats.total.visits}
          unique={stats.total.unique}
          testid="stat-total"
        />
      </div>

      {/* Daglig graf siste 30 dager */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-slate-900 mb-1">
          Besøk per dag – siste 30 dager
        </h3>
        <p className="text-xs text-slate-500 mb-4">
          Søylene viser totalt antall besøk per dag.
        </p>
        {stats.daily_last_30_days.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">
            Ingen data ennå.
          </p>
        ) : (
          <div
            className="flex items-end gap-1 h-40"
            data-testid="analytics-daily-chart"
          >
            {stats.daily_last_30_days.map((d) => (
              <div
                key={d.date}
                className="flex-1 group relative flex flex-col items-center justify-end"
                title={`${d.date}: ${d.visits} besøk (${d.unique} unike)`}
              >
                <div
                  className="w-full bg-green-500 rounded-t hover:bg-green-600 transition-colors"
                  style={{
                    height: `${Math.max(2, (d.visits / maxDaily) * 100)}%`,
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Topp sider */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">
            Mest besøkte sider – siste 30 dager
          </h3>
        </div>
        {stats.top_pages.length === 0 ? (
          <p className="text-slate-500 text-sm py-8 text-center">
            Ingen data ennå.
          </p>
        ) : (
          <table className="w-full" data-testid="analytics-top-pages">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Side
                </th>
                <th className="text-right py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Besøk
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.top_pages.map((row, idx) => (
                <tr
                  key={`${row.path}-${idx}`}
                  className="border-b border-slate-100 last:border-0"
                >
                  <td className="py-3 px-6 text-slate-900 font-mono text-sm">
                    {row.path || '/'}
                  </td>
                  <td className="py-3 px-6 text-right font-semibold text-slate-900">
                    {row.visits.toLocaleString('nb-NO')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AnalyticsTab;
